import * as Sentry from "@sentry/react";
import { User } from "@supabase/supabase-js";

/**
 * Updates Sentry user context with current user data
 * This enables auto-population of feedback forms and better error tracking
 */
export function updateSentryUserContext(user: User | null) {
  if (user) {
    // Extract user information
    const email = user.email || '';
    
    // Try to get name from various sources
    let fullName = '';
    
    // First try user_metadata fields
    if (user.user_metadata?.full_name) {
      fullName = user.user_metadata.full_name;
    } else if (user.user_metadata?.first_name || user.user_metadata?.last_name) {
      const firstName = user.user_metadata.first_name || '';
      const lastName = user.user_metadata.last_name || '';
      fullName = `${firstName} ${lastName}`.trim();
    }
    
    // If still no name, try to extract from email (but only as last resort)
    if (!fullName && email) {
      const emailPrefix = email.split('@')[0];
      // Only use email prefix if it looks like a name (not just random characters)
      if (emailPrefix && emailPrefix.length > 2 && /^[a-zA-Z]/.test(emailPrefix)) {
        fullName = emailPrefix;
      } else {
        fullName = 'User'; // Fallback to generic name
      }
    }
    
    // Final fallback
    if (!fullName) {
      fullName = 'User';
    }
    
    // Set user context in Sentry
    Sentry.setUser({
      id: user.id,
      email: email,
      username: fullName, // Try using name as username too
      // For feedback form auto-population
      name: fullName,
      // Additional metadata
      metadata: {
        first_name: user.user_metadata?.first_name || '',
        last_name: user.user_metadata?.last_name || '',
        full_name: user.user_metadata?.full_name || '',
        avatar_url: user.user_metadata?.avatar_url,
        created_at: user.created_at,
        last_sign_in_at: user.last_sign_in_at,
      }
    });
    
    // Also try setting additional context that might be used by feedback form
    Sentry.setContext('user_profile', {
      display_name: fullName,
      email: email,
      first_name: user.user_metadata?.first_name || '',
      last_name: user.user_metadata?.last_name || '',
    });

    // Set additional context tags
    Sentry.setTag('user_type', 'authenticated');
    Sentry.setTag('auth_provider', user.app_metadata?.provider || 'email');
    
    console.log('[Sentry] User context updated:', { 
      email, 
      name: fullName,
      user_metadata: user.user_metadata,
      raw_name_sources: {
        full_name: user.user_metadata?.full_name,
        first_name: user.user_metadata?.first_name,
        last_name: user.user_metadata?.last_name,
        email_prefix: email?.split('@')[0]
      }
    });
    
    // Debug: Check what Sentry actually received
    console.log('[Sentry] Debug - Sentry user object:', {
      id: user.id,
      email: email,
      username: email,
      name: fullName,
    });
  } else {
    // Clear user context when user logs out
    Sentry.setUser(null);
    Sentry.setTag('user_type', 'anonymous');
    
    console.log('[Sentry] User context cleared');
  }
}

/**
 * Sets additional context for business profile data
 */
export function updateSentryBusinessContext(businessProfile: {
  business_name?: string;
  business_slug?: string;
  website_url?: string;
} | null) {
  if (businessProfile) {
    Sentry.setContext('business', {
      name: businessProfile.business_name,
      slug: businessProfile.business_slug,
      website: businessProfile.website_url,
    });
    
    Sentry.setTag('has_business_profile', 'true');
    console.log('[Sentry] Business context updated:', businessProfile.business_name);
  } else {
    Sentry.setContext('business', null);
    Sentry.setTag('has_business_profile', 'false');
    console.log('[Sentry] Business context cleared');
  }
}

/**
 * Captures a custom event with user context
 */
export function captureUserEvent(message: string, level: 'info' | 'warning' | 'error' = 'info', extra?: Record<string, any>) {
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    if (extra) {
      scope.setContext('extra', extra);
    }
    Sentry.captureMessage(message, level);
  });
}
